<!-- eslint-disable no-undef -->
<script setup>
    // Imports
    import { ref, watch, onMounted, computed, nextTick } from 'vue';
    import JsonEditor from '@/components/common/JsonEditor.vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import { ActionV2 } from '@/api/action';
    import { useActionStream } from '@/composables/useActionStream';

    const props = defineProps({
        dataID: {
            type: String,
            required: true
        }
    });

    const { sendAction, awaitResult } = useActionStream(props.dataID);

    const prefillState = ref(null); // null | 'fetching' | 'error'

    // References
    const selectedClass = ref(null);  // e.g., "system", "network"
    const selectedAction = ref(null);  // e.g., "reboot", "wifi"
    const selectedActionType = ref(null);  // e.g., "Create", "Read", "Update", "Delete", "Execute"
    const actionDetails = ref(null);  // The full actionType details object
    const payload = ref({});
    const payloadValues = ref({});
    const payloadSchema = ref(null);  // Schema for building the form
    const editorVisible = ref(false);
    const payloadValid = ref(true);
    const loading = ref(false);
    const result = ref(null);
    const resultType = ref(null); // 'success' or 'error'
    const actions = ref({});
    const loadingActions = ref(true);

    // Lifecycle hooks
    onMounted(async() => {
        const response = await ActionV2.get();
        if (response.status === 200) {

            // Extract the actions object from the response
            actions.value = (await response.json()).actions || {};
        }
        loadingActions.value = false;
    });

    // Watches
    watch(selectedClass, () => {
        selectedAction.value = null;
        selectedActionType.value = null;
        actionDetails.value = null;
        resetPayload();
    });

    watch(selectedAction, () => {
        selectedActionType.value = null;
        actionDetails.value = null;
        resetPayload();
        if (filteredActionTypes.value.length === 1) {
            selectedActionType.value = filteredActionTypes.value[0].key;
        }
    });

    watch(selectedActionType, async () => {
        result.value = null;
        resultType.value = null;
        editorVisible.value = false;
        prefillState.value = null;

        if (selectedActionType.value && selectedClass.value && selectedAction.value) {
            const actionObj = actions.value[selectedClass.value]?.[selectedAction.value];
            if (actionObj && actionObj[selectedActionType.value]) {
                actionDetails.value = actionObj[selectedActionType.value];
                await nextTick();
                if (actionDetails.value?.prefill_from_read && selectedActionType.value === 'Update') {
                    await runPrefillRead();
                } else {
                    await setPayload();
                }
            }
        } else {
            actionDetails.value = null;
            resetPayload();
        }
    });

    // Methods
    const sortedClasses = computed(() => {
        return Object.entries(actions.value)
            .filter(([key]) => key !== 'version' && key !== 'app' && key !== 'config')
            .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''));
    });

    const filteredActions = computed(() => {
        if (!selectedClass.value || !actions.value[selectedClass.value]) return [];
        
        return Object.entries(actions.value[selectedClass.value])
            .filter(([key]) => key !== 'name')
            .sort(([a], [b]) => {
                const nameA = actions.value[selectedClass.value][a]?.name || a;
                const nameB = actions.value[selectedClass.value][b]?.name || b;
                return nameA.localeCompare(nameB);
            });
    });

    const filteredActionTypes = computed(() => {
        if (!selectedClass.value || !selectedAction.value || !actions.value[selectedClass.value]?.[selectedAction.value]) {
            return [];
        }
        
        const actionObj = actions.value[selectedClass.value][selectedAction.value];
        const actionTypes = ['Create', 'Read', 'Update', 'Delete', 'Execute'];
        
        return actionTypes
            .filter(type => actionObj[type])
            .map(type => ({
                key: type,
                value: actionObj[type],
                description: actionObj[type].description || ''
            }));
    });

    const hasActions = computed(() => {
        return Object.keys(actions.value).length > 0;
    });

    const getActionFullList = (classObj) => {
        return Object.entries(classObj)
            .filter(([k]) => k !== 'name')
            .map(([k, v]) => v.name || k)
            .sort((a, b) => a.localeCompare(b));
    };

    const getActionOverflowList = (classObj) => {
        return getActionFullList(classObj).slice(3);
    };

    const getTypeLabelClass = (type) => ({
        Create: 'text-green-700 dark:text-green-400',
        Read: 'text-mc-blue',
        Update: 'text-amber-700 dark:text-amber-400',
        Delete: 'text-mc-red',
        Execute: 'text-mc-ink dark:text-on-dark',
    }[type] ?? 'text-mc-ink dark:text-on-dark');

    const getTypeUnselectedClass = (type) => ({
        Create: 'border-green-500/40 dark:border-green-500/30 hover:border-green-500 bg-green-500/5 dark:bg-green-500/10',
        Read: 'border-mc-blue/40 hover:border-mc-blue bg-mc-blue/5 dark:bg-mc-blue/10',
        Update: 'border-amber-500/40 dark:border-amber-500/30 hover:border-amber-500 bg-amber-500/5 dark:bg-amber-500/10',
        Delete: 'border-mc-red/40 hover:border-mc-red bg-mc-red/5 dark:bg-mc-red/10',
        Execute: 'border-mc-steel dark:border-line-on-dark hover:border-mc-ink dark:hover:border-on-dark-muted bg-transparent',
    }[type] ?? 'border-mc-steel bg-transparent');

    const getTypeSelectedClass = (type) => ({
        Create: 'border-green-600 dark:border-green-500 bg-green-600 dark:bg-green-500',
        Read: 'border-mc-blue bg-mc-blue',
        Update: 'border-amber-600 dark:border-amber-500 bg-amber-600 dark:bg-amber-500',
        Delete: 'border-mc-red bg-mc-red',
        Execute: 'border-mc-ink dark:border-on-dark bg-mc-ink dark:bg-on-dark',
    }[type] ?? 'border-mc-ink bg-mc-ink');

    const getAvailableTypes = (actionKey) => {
        if (!selectedClass.value || !actions.value[selectedClass.value]?.[actionKey]) return [];
        const actionObj = actions.value[selectedClass.value][actionKey];
        return ['Create', 'Read', 'Update', 'Delete', 'Execute'].filter(t => actionObj[t]);
    };

    const resetPayload = () => {
        payload.value = {};
        payloadValues.value = {};
        payloadSchema.value = null;
        editorVisible.value = false;
        payloadValid.value = true;
    }

    const setPayload = async () => {
        if (!actionDetails.value) {
            resetPayload();
            return;
        }

        const payloadDef = actionDetails.value.payload;

        // Payload is empty string or simple string
        if (typeof payloadDef === 'string') {
            payloadValues.value = {};
            payload.value = payloadDef || '';
            payloadSchema.value = null;
            editorVisible.value = false;
            return;
        }

        // Payload is empty object
        if (typeof payloadDef === 'object' && payloadDef !== null && Object.keys(payloadDef).length === 0) {
            payloadValues.value = {};
            payload.value = {};
            payloadSchema.value = null;
            editorVisible.value = false;
            return;
        }

        // Payload is a non-empty object — two supported formats:
        //   Wrapped: { WifiConfig: { ssid: { type: 'string' }, ... } }  → first value is an object WITHOUT 'type'
        //   Flat:    { app_id: { type: 'string', description: '...' } } → first value IS a field def (HAS 'type')
        if (typeof payloadDef === 'object' && payloadDef !== null) {
            const firstValue = Object.values(payloadDef)[0];
            // A wrapped format has a container object as the first value (no 'type' at that level).
            // A flat format has field definitions as values — each with 'type' directly on it.
            const isWrapped = firstValue !== null
                && typeof firstValue === 'object'
                && !Array.isArray(firstValue)
                && !('type' in firstValue);

            const initialValues = {};
            const buildInitialValues = (schema) => {
                for (const [key, fieldDef] of Object.entries(schema)) {
                    if (fieldDef && typeof fieldDef === 'object' && fieldDef.type) {
                        // Field definition: { type: 'string', description: '...', ... }
                        initialValues[key] = (fieldDef.type === 'number' || fieldDef.type === 'integer') ? 0
                                           : fieldDef.type === 'boolean' ? false
                                           : '';
                    } else if (typeof fieldDef === 'string') {
                        // Shorthand type string: "string", "number", "boolean"
                        initialValues[key] = (fieldDef === 'number' || fieldDef === 'integer') ? 0
                                           : fieldDef === 'boolean' ? false
                                           : '';
                    } else if (fieldDef && typeof fieldDef === 'object') {
                        // Nested object
                        initialValues[key] = {};
                        buildInitialValues(fieldDef);
                    }
                }
            };

            if (isWrapped) {
                const actionKey = Object.keys(payloadDef)[0];
                payloadSchema.value = payloadDef[actionKey];
                buildInitialValues(payloadSchema.value);
            } else {
                payloadSchema.value = payloadDef;
                buildInitialValues(payloadDef);
            }

            payloadValues.value = initialValues;
            editorVisible.value = true;
            return;
        }

        resetPayload();
    }

    const extractPrefillValues = (resultPayload) => {
        if (!resultPayload || typeof resultPayload !== 'object' || Array.isArray(resultPayload)) return {};
        // If the schema has a wrapper key (e.g. "WifiConfig") and the result uses it, unwrap it.
        // Otherwise the device returned a flat object — use it directly.
        const payloadDef = actionDetails.value?.payload;
        if (payloadDef && typeof payloadDef === 'object' && !Array.isArray(payloadDef)) {
            const schemaKey = Object.keys(payloadDef)[0];
            const wrapped = schemaKey && resultPayload[schemaKey];
            if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) {
                return { ...wrapped };
            }
        }
        return { ...resultPayload };
    };

    const runPrefillRead = async () => {
        prefillState.value = 'fetching';
        editorVisible.value = false;
        try {
            const { ok, action_id } = await sendAction({
                action_class: selectedClass.value,
                action:       selectedAction.value,
                action_type:  'Read',
                payload:      {},
            });
            if (!ok) throw new Error('Read request failed');
            const resultPayload = await awaitResult(action_id);
            payloadValues.value = extractPrefillValues(resultPayload);
            prefillState.value = null;
            editorVisible.value = true;
        } catch (_) {
            prefillState.value = 'error';
        }
    };

    const resetForm = () => {
        selectedClass.value = null;
        selectedAction.value = null;
        selectedActionType.value = null;
        actionDetails.value = null;
        prefillState.value = null;
        resetPayload();
    }

    const sendRequest = async () => {
        loading.value = true;
        result.value = null;
        resultType.value = null;

        let finalPayload = {};

        if (actionDetails.value && 'payload' in actionDetails.value) {
            const payloadDef = actionDetails.value.payload;

            if (typeof payloadDef === 'string') {
                finalPayload = payloadDef;
            } else if (typeof payloadDef === 'object' && payloadDef !== null && Object.keys(payloadDef).length === 0) {
                // Schema is empty, but prefill_from_read may have populated payloadValues — include it.
                finalPayload = Object.keys(payloadValues.value).length > 0 ? { ...payloadValues.value } : {};
            } else if (typeof payloadDef === 'object' && payloadDef !== null) {
                const firstValue = Object.values(payloadDef)[0];
                const isWrapped = firstValue !== null
                    && typeof firstValue === 'object'
                    && !Array.isArray(firstValue)
                    && !('type' in firstValue);
                const cleanValues = {};
                for (const [key, value] of Object.entries(payloadValues.value)) {
                    if (value !== '' && value !== null && value !== undefined) {
                        cleanValues[key] = value;
                    }
                }
                if (isWrapped) {
                    const actionKey = Object.keys(payloadDef)[0];
                    finalPayload = { [actionKey]: Object.keys(cleanValues).length > 0 ? cleanValues : {} };
                } else {
                    finalPayload = Object.keys(cleanValues).length > 0 ? cleanValues : {};
                }
            }
        }

        try {
            const { ok, message } = await sendAction({
                action_class: selectedClass.value,
                action:       selectedAction.value,
                action_type:  selectedActionType.value,
                payload:      finalPayload,
            });

            if (ok) {
                result.value = message || 'Request sent successfully';
                resultType.value = 'success';
                setTimeout(() => {
                    if (resultType.value === 'success') {
                        result.value = null;
                        resultType.value = null;
                    }
                }, 3000);
            } else {
                result.value = message || 'Request failed';
                resultType.value = 'error';
            }
        } catch (error) {
            result.value = error.message || 'An error occurred';
            resultType.value = 'error';
        } finally {
            loading.value = false;
        }
    }

    const handleKeyPress = (event) => {
        if (event.target.tagName === 'TEXTAREA') return;
        if (event.key === 'Enter' && !event.shiftKey && selectedActionType.value && !loading.value) {
            event.preventDefault();
            sendRequest();
        }
    }
</script>

<template>
    <div class="flex flex-col gap-4" @keydown="handleKeyPress">

        <!-- Loading -->
        <div v-if="loadingActions" class="flex flex-col items-center justify-center py-10 gap-3">
            <LoadingAnimation3 size="36px" />
            <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Loading available actions…</p>
        </div>

        <!-- No actions -->
        <div v-else-if="!hasActions" class="flex flex-col items-center justify-center py-10 text-center gap-2">
            <p class="font-body font-semibold text-sm text-mc-ink dark:text-on-dark">No actions available</p>
            <p class="font-body text-xs text-ink-muted dark:text-on-dark-muted">Please try again later</p>
        </div>

        <!-- Form -->
        <form v-else id="actions-request-v2" class="flex flex-col gap-5" @submit.prevent="sendRequest">

            <!-- Step 1: Action Class cards -->
            <div>
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Action Class</div>
                <div class="grid grid-cols-2 gap-2">
                    <button
                        v-for="[classKey, classDetails] in sortedClasses"
                        :key="classKey"
                        type="button"
                        :disabled="loading"
                        class="relative text-left px-3 py-2.5 rounded-mc-md border transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                        :class="selectedClass === classKey
                            ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                            : ['border-mc-steel dark:border-line-on-dark bg-transparent hover:border-mc-ink dark:hover:border-on-dark-muted', getActionOverflowList(classDetails).length ? 'group hover:rounded-b-none hover:z-10' : '']"
                        @click="selectedClass = selectedClass === classKey ? null : classKey"
                    >
                        <div class="font-display font-bold text-sm text-mc-ink dark:text-on-dark">{{ classDetails.name || classKey }}</div>
                        <div class="font-mono text-xs text-ink-muted dark:text-on-dark-muted mt-1 truncate">
                            <template v-for="(name, i) in getActionFullList(classDetails).slice(0, 3)" :key="name">{{ i > 0 ? ' · ' : '' }}{{ name }}</template><span v-if="getActionOverflowList(classDetails).length" class="transition-opacity duration-200 ease-out group-hover:opacity-0"> · More...</span>
                        </div>
                        <!-- Hover overlay: overflow items only, card text stays in place -->
                        <div
                            v-if="getActionOverflowList(classDetails).length"
                            class="absolute top-full left-[-1px] right-[-1px] border border-t-0 rounded-b-mc-md px-3 pb-2.5 overflow-hidden max-h-0 opacity-0 group-hover:max-h-[20rem] group-hover:opacity-100 group-hover:border-mc-ink dark:group-hover:border-on-dark-muted transition-[max-height,opacity,border-color] duration-200 ease-in-out pointer-events-none z-20"
                            :class="selectedClass === classKey
                                ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                                : 'border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-mc-charcoal'"
                        >
                            <template v-for="(name, i) in getActionOverflowList(classDetails)" :key="name">
                                <span class="inline-block whitespace-nowrap font-mono text-xs text-ink-muted dark:text-on-dark-muted">{{ i === 0 ? ' · ' : '' }}{{ name }}</span>{{ i < getActionOverflowList(classDetails).length - 1 ? ' · ' : '' }}
                            </template>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Step 2: Action rows (after class selected) -->
            <div v-if="selectedClass">
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Action</div>
                <div class="flex flex-col gap-1.5">
                    <button
                        v-for="[actionKey, actionData] in filteredActions"
                        :key="actionKey"
                        type="button"
                        :disabled="loading"
                        class="flex items-center justify-between px-3 py-2.5 rounded-mc-sm border transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                        :class="selectedAction === actionKey
                            ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                            : 'border-mc-steel dark:border-line-on-dark hover:border-mc-ink dark:hover:border-on-dark-muted bg-transparent'"
                        @click="selectedAction = actionKey"
                    >
                        <span class="font-body text-sm text-mc-ink dark:text-on-dark">{{ actionData.name || actionKey }}</span>
                        <div class="flex gap-1 ml-2 shrink-0">
                            <span
                                v-for="type in getAvailableTypes(actionKey)"
                                :key="type"
                                class="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] font-bold uppercase"
                                :class="{
                                    'bg-green-500/15 text-green-700 dark:text-green-400': type === 'Create',
                                    'bg-mc-blue/15 text-mc-blue': type === 'Read',
                                    'bg-amber-500/15 text-amber-700 dark:text-amber-400': type === 'Update',
                                    'bg-mc-red/15 text-mc-red': type === 'Delete',
                                    'bg-mc-ink/10 dark:bg-white/15 text-mc-ink dark:text-on-dark': type === 'Execute',
                                }"
                            >{{ type[0] }}</span>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Step 3: Action Type rows (after action selected) -->
            <div v-if="selectedAction">
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Action Type</div>
                <div class="flex flex-col gap-1.5">
                    <button
                        v-for="actionType in filteredActionTypes"
                        :key="actionType.key"
                        type="button"
                        :disabled="loading"
                        class="flex items-start gap-3 text-left px-3 py-2 rounded-mc-sm border transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                        :class="selectedActionType === actionType.key
                            ? getTypeSelectedClass(actionType.key)
                            : getTypeUnselectedClass(actionType.key)"
                        @click="selectedActionType = actionType.key"
                    >
                        <span
                            class="font-mono text-xs font-bold uppercase tracking-[0.06em] shrink-0 mt-px"
                            :class="selectedActionType === actionType.key ? 'text-white dark:text-mc-ink' : getTypeLabelClass(actionType.key)"
                        >{{ actionType.key }}</span>
                        <span
                            v-if="actionType.description"
                            class="font-body text-xs leading-relaxed"
                            :class="selectedActionType === actionType.key ? 'text-white/80 dark:text-mc-ink/70' : 'text-ink-muted dark:text-on-dark-muted'"
                        >{{ actionType.description }}</span>
                    </button>
                </div>
            </div>

            <!-- Prefill: fetching state -->
            <div
                v-if="prefillState === 'fetching'"
                class="flex items-center gap-3 py-4 px-3 bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md"
            >
                <LoadingAnimation3 size="20px" />
                <span class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Fetching current configuration…</span>
            </div>

            <!-- Prefill: error state -->
            <div
                v-else-if="prefillState === 'error'"
                class="flex items-center justify-between gap-3 py-3 px-3 bg-mc-cloud dark:bg-mc-charcoal border border-mc-red/40 rounded-mc-md"
            >
                <span class="font-body text-sm text-mc-red">Failed to fetch current configuration.</span>
                <button
                    type="button"
                    class="btn-secondary text-xs py-1 px-2"
                    @click="runPrefillRead"
                >
                    Retry
                </button>
            </div>

            <!-- Payload editor (normal flow or post-prefill) -->
            <template v-else>
                <transition
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="opacity-0 -translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-1"
                >
                    <div v-if="editorVisible" class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-3">
                        <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Payload</div>
                        <JsonEditor
                            v-model:json-value="payloadValues"
                            @update:valid="payloadValid = $event"
                            title="Payload"
                            :advanced="true"
                            :schema="payloadSchema"
                        />
                    </div>
                </transition>
            </template>

            <!-- Send row -->
            <div class="flex items-center gap-3 pt-1 border-t border-mc-steel/50 dark:border-line-on-dark">
                <button
                    type="submit"
                    class="btn-primary"
                    :disabled="!selectedActionType || loading || prefillState === 'fetching' || !payloadValid"
                >
                    {{ loading ? 'Sending…' : 'Send Request' }}
                </button>

                <button
                    v-if="selectedClass"
                    type="button"
                    class="btn-secondary"
                    :disabled="loading"
                    @click="resetForm"
                >
                    Reset
                </button>

                <LoadingAnimation3 v-if="loading" size="24px" />

                <transition
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                >
                    <span
                        v-if="result"
                        class="font-body text-sm font-semibold"
                        :class="resultType === 'success' ? 'text-green-600 dark:text-green-400' : 'text-mc-red'"
                    >
                        {{ result }}
                    </span>
                </transition>
            </div>

        </form>
    </div>
</template>